import { BlogPost, BlogCategory } from "@/app/types/blog";

type FetchParams = {
  page?: number;
  perPage?: number;
  search?: string;
  category?: string;
  tagSlug?: string;
};

type CacheState<T> = {
  value: T | null;
  promise: Promise<T> | null;
};

const categorySummaryCache: CacheState<BlogCategorySummary> = {
  value: null,
  promise: null,
};

const popularPostsCache = new Map<number, CacheState<BlogPost[]>>();

const totalViewsCache: CacheState<number> = {
  value: null,
  promise: null,
};

const postsCache = new Map<string, CacheState<FetchBlogPostsResult>>();

const getPostsCacheState = (key: string): CacheState<FetchBlogPostsResult> => {
  if (!postsCache.has(key)) {
    postsCache.set(key, { value: null, promise: null });
  }
  return postsCache.get(key)!;
};

const resolveCache = async <T>(
  state: CacheState<T>,
  fetcher: () => Promise<T>,
  forceRefresh = false
): Promise<T> => {
  if (!forceRefresh && state.value !== null) {
    return state.value;
  }

  if (!forceRefresh && state.promise) {
    return state.promise;
  }

  const promise = fetcher()
    .then((result) => {
      state.value = result;
      return result;
    })
    .finally(() => {
      state.promise = null;
    });

  state.promise = promise;
  return promise;
};

const resolveBaseUrl = (): string => {
  if (typeof window === "undefined") {
    return (
      process.env.BLOG_API_URL || "https://blog-middleware.temok.com/api"
    );
  }
  return (

    process.env.BLOG_API_URL ||"https://blog-middleware.temok.com/api"
  );
};

const BLOG_API_BASE_URL = resolveBaseUrl().replace(/\/+$/, "");

const decodeHtmlEntities = (value: string): string =>
  value
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&ldquo;/g, "“")
    .replace(/&rdquo;/g, "”")
    .replace(/&lsquo;/g, "‘")
    .replace(/&rsquo;/g, "’");

// Lightweight MD5 implementation to support Gravatar hashing without external deps
const md5 = (value: string): string => {
  const utf8 = new TextEncoder().encode(value);

  const leftRotate = (x: number, c: number) => (x << c) | (x >>> (32 - c));

  const toWordArray = (bytes: Uint8Array) => {
    const length = bytes.length;
    const nBlocks = ((length + 8) >>> 6 << 4) + 16;
    const words = new Array<number>(nBlocks).fill(0);

    for (let i = 0; i < length; i += 1) {
      words[i >> 2] |= bytes[i]! << ((i % 4) * 8);
    }

    words[length >> 2] |= 0x80 << ((length % 4) * 8);
    words[nBlocks - 2] = length * 8;
    return words;
  };

  const add = (a: number, b: number) => {
    const l = (a & 0xffff) + (b & 0xffff);
    const m = (a >> 16) + (b >> 16) + (l >> 16);
    return (m << 16) | (l & 0xffff);
  };

  const F = (x: number, y: number, z: number) => (x & y) | (~x & z);
  const G = (x: number, y: number, z: number) => (x & z) | (y & ~z);
  const H = (x: number, y: number, z: number) => x ^ y ^ z;
  const I = (x: number, y: number, z: number) => y ^ (x | ~z);

  const FF = (
    a: number,
    b: number,
    c: number,
    d: number,
    x: number,
    s: number,
    ac: number
  ) => add(leftRotate(add(add(a, F(b, c, d)), add(x, ac)), s), b);
  const GG = (
    a: number,
    b: number,
    c: number,
    d: number,
    x: number,
    s: number,
    ac: number
  ) => add(leftRotate(add(add(a, G(b, c, d)), add(x, ac)), s), b);
  const HH = (
    a: number,
    b: number,
    c: number,
    d: number,
    x: number,
    s: number,
    ac: number
  ) => add(leftRotate(add(add(a, H(b, c, d)), add(x, ac)), s), b);
  const II = (
    a: number,
    b: number,
    c: number,
    d: number,
    x: number,
    s: number,
    ac: number
  ) => add(leftRotate(add(add(a, I(b, c, d)), add(x, ac)), s), b);

  const x = toWordArray(utf8);

  let a = 0x67452301;
  let b = 0xefcdab89;
  let c = 0x98badcfe;
  let d = 0x10325476;

  for (let i = 0; i < x.length; i += 16) {
    const [aa, bb, cc, dd] = [a, b, c, d];

    a = FF(a, b, c, d, x[i]!, 7, 0xd76aa478);
    d = FF(d, a, b, c, x[i + 1]!, 12, 0xe8c7b756);
    c = FF(c, d, a, b, x[i + 2]!, 17, 0x242070db);
    b = FF(b, c, d, a, x[i + 3]!, 22, 0xc1bdceee);
    a = FF(a, b, c, d, x[i + 4]!, 7, 0xf57c0faf);
    d = FF(d, a, b, c, x[i + 5]!, 12, 0x4787c62a);
    c = FF(c, d, a, b, x[i + 6]!, 17, 0xa8304613);
    b = FF(b, c, d, a, x[i + 7]!, 22, 0xfd469501);
    a = FF(a, b, c, d, x[i + 8]!, 7, 0x698098d8);
    d = FF(d, a, b, c, x[i + 9]!, 12, 0x8b44f7af);
    c = FF(c, d, a, b, x[i + 10]!, 17, 0xffff5bb1);
    b = FF(b, c, d, a, x[i + 11]!, 22, 0x895cd7be);
    a = FF(a, b, c, d, x[i + 12]!, 7, 0x6b901122);
    d = FF(d, a, b, c, x[i + 13]!, 12, 0xfd987193);
    c = FF(c, d, a, b, x[i + 14]!, 17, 0xa679438e);
    b = FF(b, c, d, a, x[i + 15]!, 22, 0x49b40821);

    a = GG(a, b, c, d, x[i + 1]!, 5, 0xf61e2562);
    d = GG(d, a, b, c, x[i + 6]!, 9, 0xc040b340);
    c = GG(c, d, a, b, x[i + 11]!, 14, 0x265e5a51);
    b = GG(b, c, d, a, x[i]!, 20, 0xe9b6c7aa);
    a = GG(a, b, c, d, x[i + 5]!, 5, 0xd62f105d);
    d = GG(d, a, b, c, x[i + 10]!, 9, 0x02441453);
    c = GG(c, d, a, b, x[i + 15]!, 14, 0xd8a1e681);
    b = GG(b, c, d, a, x[i + 4]!, 20, 0xe7d3fbc8);
    a = GG(a, b, c, d, x[i + 9]!, 5, 0x21e1cde6);
    d = GG(d, a, b, c, x[i + 14]!, 9, 0xc33707d6);
    c = GG(c, d, a, b, x[i + 3]!, 14, 0xf4d50d87);
    b = GG(b, c, d, a, x[i + 8]!, 20, 0x455a14ed);
    a = GG(a, b, c, d, x[i + 13]!, 5, 0xa9e3e905);
    d = GG(d, a, b, c, x[i + 2]!, 9, 0xfcefa3f8);
    c = GG(c, d, a, b, x[i + 7]!, 14, 0x676f02d9);
    b = GG(b, c, d, a, x[i + 12]!, 20, 0x8d2a4c8a);

    a = HH(a, b, c, d, x[i + 5]!, 4, 0xfffa3942);
    d = HH(d, a, b, c, x[i + 8]!, 11, 0x8771f681);
    c = HH(c, d, a, b, x[i + 11]!, 16, 0x6d9d6122);
    b = HH(b, c, d, a, x[i + 14]!, 23, 0xfde5380c);
    a = HH(a, b, c, d, x[i + 1]!, 4, 0xa4beea44);
    d = HH(d, a, b, c, x[i + 4]!, 11, 0x4bdecfa9);
    c = HH(c, d, a, b, x[i + 7]!, 16, 0xf6bb4b60);
    b = HH(b, c, d, a, x[i + 10]!, 23, 0xbebfbc70);
    a = HH(a, b, c, d, x[i + 13]!, 4, 0x289b7ec6);
    d = HH(d, a, b, c, x[i]!, 11, 0xeaa127fa);
    c = HH(c, d, a, b, x[i + 3]!, 16, 0xd4ef3085);
    b = HH(b, c, d, a, x[i + 6]!, 23, 0x04881d05);
    a = HH(a, b, c, d, x[i + 9]!, 4, 0xd9d4d039);
    d = HH(d, a, b, c, x[i + 12]!, 11, 0xe6db99e5);
    c = HH(c, d, a, b, x[i + 15]!, 16, 0x1fa27cf8);
    b = HH(b, c, d, a, x[i + 2]!, 23, 0xc4ac5665);

    a = II(a, b, c, d, x[i]!, 6, 0xf4292244);
    d = II(d, a, b, c, x[i + 7]!, 10, 0x432aff97);
    c = II(c, d, a, b, x[i + 14]!, 15, 0xab9423a7);
    b = II(b, c, d, a, x[i + 5]!, 21, 0xfc93a039);
    a = II(a, b, c, d, x[i + 12]!, 6, 0x655b59c3);
    d = II(d, a, b, c, x[i + 3]!, 10, 0x8f0ccc92);
    c = II(c, d, a, b, x[i + 10]!, 15, 0xffeff47d);
    b = II(b, c, d, a, x[i + 1]!, 21, 0x85845dd1);
    a = II(a, b, c, d, x[i + 8]!, 6, 0x6fa87e4f);
    d = II(d, a, b, c, x[i + 15]!, 10, 0xfe2ce6e0);
    c = II(c, d, a, b, x[i + 6]!, 15, 0xa3014314);
    b = II(b, c, d, a, x[i + 13]!, 21, 0x4e0811a1);
    a = II(a, b, c, d, x[i + 4]!, 6, 0xf7537e82);
    d = II(d, a, b, c, x[i + 11]!, 10, 0xbd3af235);
    c = II(c, d, a, b, x[i + 2]!, 15, 0x2ad7d2bb);
    b = II(b, c, d, a, x[i + 9]!, 21, 0xeb86d391);

    a = add(a, aa);
    b = add(b, bb);
    c = add(c, cc);
    d = add(d, dd);
  }

  const toHex = (num: number) =>
    (`00000000${num.toString(16)}`.slice(-8).match(/../g) || []).reverse().join("");

  return [a, b, c, d].map(toHex).join("");
};

const slugify = (value: string): string =>
  value
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const stripHtml = (value: string): string =>
  value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

const buildUrl = (
  path: string,
  params?: Record<string, string | number | undefined>
) => {
  const url = new URL(
    `${BLOG_API_BASE_URL}/${path.replace(/^\//, "")}`
  );
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        url.searchParams.set(key, String(value));
      }
    });
  }
  return url.toString();
};

const extractTitle = (raw: unknown): string => {
  if (!raw) return "";
  if (typeof raw === "string") return raw;
  if (typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    return (
      (typeof obj.rendered === "string" && obj.rendered) ||
      (typeof obj.plain === "string" && obj.plain) ||
      (typeof obj.value === "string" && obj.value) ||
      ""
    );
  }
  return "";
};

const extractAuthor = (raw: unknown): { name: string; avatar: string } => {
  if (!raw) {
    return {
      name: "Unknown Author",
      avatar: "https://i.pravatar.cc/150?u=temok",
    };
  }

  if (typeof raw === "string") {
    return {
      name: raw,
      avatar: `https://i.pravatar.cc/150?u=${encodeURIComponent(raw)}`,
    };
  }

  if (typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    const name =
      (typeof obj.name === "string" && obj.name) ||
      (typeof obj.fullName === "string" && obj.fullName) ||
      (typeof obj.displayName === "string" && obj.displayName) ||
      (typeof obj.title === "string" && obj.title) ||
      "Unknown Author";
    const avatar =
      (typeof obj.avatar === "string" && obj.avatar) ||
      (typeof obj.photo === "string" && obj.photo) ||
      (typeof obj.image === "string" && obj.image) ||
      (typeof obj.picture === "string" && obj.picture) ||
      undefined;
    return {
      name,
      avatar: avatar || `https://i.pravatar.cc/150?u=${encodeURIComponent(name)}`,
    };
  }

  return {
    name: "Unknown Author",
    avatar: "https://i.pravatar.cc/150?u=temok",
  };
};

const normalisePost = (raw: Record<string, unknown>): BlogPost => {
  const idCandidate =
    raw.id ??
    raw.ID ??
    raw.post_id ??
    raw.postId ??
    raw.postid ??
    slugify(String(raw.title ?? raw.post_title ?? Date.now()));

  const rawTitle = extractTitle(raw.title ?? raw.post_title ?? "");
  const rawExcerpt = extractTitle(raw.excerpt ?? raw.summary ?? raw.post_excerpt ?? "");
  const rawContent =
    extractTitle(raw.content ?? raw.post_content ?? "") ||
    (typeof raw.html === "string" ? raw.html : "");

  const author = extractAuthor(
    raw.author ??
      raw.author_name ??
      raw.authorName ??
      raw.writer ??
      raw.created_by
  );

  const authorEmail =
    (typeof raw.author_email === "string" && raw.author_email.trim()) ||
    (typeof raw.authorEmail === "string" && raw.authorEmail.trim()) ||
    "";

  const authorAvatar =
    authorEmail && authorEmail.includes("@")
      ? `https://www.gravatar.com/avatar/${md5(
          authorEmail.toLowerCase()
        )}?s=160&d=identicon`
      : undefined;

  const rawSlugCandidate =
    (typeof raw.slug === "string" && raw.slug.trim()) ||
    (typeof raw.post_name === "string" && raw.post_name.trim()) ||
    (typeof raw.permalink === "string" &&
      raw.permalink.split("/").filter(Boolean).pop()) ||
    (typeof raw.link === "string" &&
      raw.link.split("/").filter(Boolean).pop()) ||
    "";

  const slug =
    rawSlugCandidate.length > 0
      ? decodeHtmlEntities(rawSlugCandidate.replace(/^\/+|\/+$/g, ""))
      : slugify(rawTitle || String(idCandidate));

  const image =
    (typeof raw.image === "string" && raw.image) ||
    (typeof raw.featured_image_url === "string" && raw.featured_image_url) ||
    (typeof raw.featuredImage === "string" && raw.featuredImage) ||
    (typeof raw.thumbnail === "string" && raw.thumbnail) ||
    undefined;

  const category =
    (typeof raw.category === "string" && raw.category) ||
    (typeof raw.category_name === "string" && raw.category_name) ||
    (Array.isArray(raw.categories) &&
      typeof raw.categories[0] === "string" &&
      raw.categories[0]) ||
    (Array.isArray(raw.categories) &&
      typeof raw.categories[0] === "object" &&
      typeof (raw.categories[0] as Record<string, unknown>).name === "string" &&
      (raw.categories[0] as Record<string, unknown>).name) ||
    "General";

  const tagsSource = raw.tags ?? raw.keywords ?? [];
  const tags =
    Array.isArray(tagsSource)
      ? tagsSource
          .map((tag) =>
            typeof tag === "string"
              ? tag
              : (tag as Record<string, unknown>).name?.toString() ?? ""
          )
          .filter(Boolean)
      : [];

  const rawPublished =
    raw.publishedAt ??
    raw.published_at ??
    raw.publish_date ??
    raw.post_date ??
    raw.date ??
    raw.date_gmt ??
    raw.created_at ??
    raw.modified ??
    raw.updated_at ??
    null;

  const safePublished = (() => {
    if (typeof rawPublished === "string" && rawPublished.trim().length > 3) {
      const parsed = new Date(rawPublished);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.toISOString();
      }
    }
    const fallback = new Date();
    return fallback.toISOString();
  })();

  const publishedAt = safePublished;

  const contentText = stripHtml(rawContent);
  const wordCount = contentText ? contentText.split(/\s+/).length : 0;
  const computedReadTime = `${Math.max(1, Math.ceil(wordCount / 200))} min read`;

  const readTime =
    (typeof raw.readTime === "string" && raw.readTime) ||
    (typeof raw.read_time === "string" && raw.read_time) ||
    computedReadTime;

  const viewsRaw =
    raw.views ??
    raw.pageViews ??
    raw.page_views ??
    raw.pageviews ??
    raw.total_views ??
    raw.totalViews ??
    0;

  const views = Number(
    typeof viewsRaw === "string" ? viewsRaw.replace(/,/g, "") : viewsRaw
  );

  const excerptText: string =
    rawExcerpt || (contentText ? contentText.slice(0, 180) : "");
  const contentValue: string = rawContent || rawExcerpt || rawTitle;

  const resolvedAuthor = {
    name:
      author.name ||
      (typeof raw.author_name === "string" && raw.author_name) ||
      "Unknown Author",
    avatar:
      authorAvatar ||
      author.avatar ||
      "https://i.pravatar.cc/150?u=temok",
  };

  return {
    id: String(idCandidate),
    slug,
    title: rawTitle || slug,
    excerpt: excerptText,
    content: contentValue,
    author: resolvedAuthor,
    avatar: resolvedAuthor.avatar,
    category: category || "General",
    tags,
    publishedAt,
    readTime,
    views,
    image: image?.replace("www.temok.com/blog", "blog.temok.com") || "https://placehold.co/1200x630/0b1d51/ffffff?text=Temok+Blog",
    metaTitle:
      (typeof raw.meta_title === "string" && raw.meta_title) ||
      (typeof raw.seo_title === "string" && raw.seo_title) ||
      undefined,
    metaDescription:
      (typeof raw.meta_description === "string" && raw.meta_description) ||
      (typeof raw.description === "string" && raw.description) ||
      undefined,
  } as BlogPost;
};

const pickPostsArray = (payload: unknown): Record<string, unknown>[] => {
  if (Array.isArray(payload)) {
    return payload as Record<string, unknown>[];
  }

  if (payload && typeof payload === "object") {
    const obj = payload as Record<string, unknown>;
    if (Array.isArray(obj.data)) {
      return obj.data as Record<string, unknown>[];
    }
    const candidates: unknown[] = [
      obj.posts,
      obj.items,
      obj.records,
      obj.result,
    ];

    if (obj.data && typeof obj.data === "object") {
      const dataObj = obj.data as Record<string, unknown>;
      candidates.push(dataObj.posts, dataObj.items, dataObj.data);
    }

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate as Record<string, unknown>[];
      }
    }
  }
  return [];
};

export interface FetchBlogPostsResult {
  posts: BlogPost[];
  total: number;
  totalPages?: number;
  totalViews?: number;
}

export const fetchBlogPosts = async (
  params: FetchParams = {},
  options: { forceRefresh?: boolean } = {}
): Promise<FetchBlogPostsResult> => {
  const { page = 1, perPage = 30, search = "" } = params;
  const normalisedKey = JSON.stringify({
    page,
    perPage,
    search: search.trim().toLowerCase(),
    category: params.category?.trim().toLowerCase() ?? "",
    tagSlug: params.tagSlug?.trim().toLowerCase() ?? "",
  });

  const cacheState = getPostsCacheState(normalisedKey);

  return resolveCache(
    cacheState,
    async () => {
      try {
        const response = await fetch(
          buildUrl("blog-posts", {
            page,
            perPage,
            search: search.trim() ? search : undefined,
            category:
              params.category && params.category.trim().length > 0
                ? params.category
                : undefined,
            tagSlug:
              params.tagSlug && params.tagSlug.trim().length > 0
                ? params.tagSlug
                : undefined,
          }),
          { cache: "no-store" }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch blog posts: ${response.statusText}`);
        }

        const json = await response.json();
        const postsRaw = pickPostsArray(json);
        const posts = postsRaw.map(normalisePost);

        const jsonObj = json as Record<string, unknown>;
        const pagination = (jsonObj.pagination ?? jsonObj.meta) as
          | Record<string, unknown>
          | undefined;

        const totalCandidates = [
          jsonObj.total,
          jsonObj.totalPosts,
          jsonObj.count,
          pagination?.total,
          pagination?.totalPosts,
          pagination?.total_records,
        ];

        const total =
          totalCandidates
            .map((candidate) =>
              typeof candidate === "string"
                ? Number(candidate)
                : typeof candidate === "number"
                ? candidate
                : undefined
            )
            .find((value) => typeof value === "number" && !Number.isNaN(value)) ??
          posts.length;

        const totalPagesCandidates = [
          jsonObj.totalPages,
          pagination?.totalPages,
          pagination?.total_pages,
        ];

        const totalPages =
          totalPagesCandidates
            .map((candidate) =>
              typeof candidate === "string"
                ? Number(candidate)
                : typeof candidate === "number"
                ? candidate
                : undefined
            )
            .find((value) => typeof value === "number" && !Number.isNaN(value)) ??
          undefined;

        const totalViewsCandidates = [
          jsonObj.total_views,
          jsonObj.totalViews,
          pagination?.total_views,
          pagination?.totalViews,
        ];

        const totalViews =
          totalViewsCandidates
            .map((candidate) =>
              typeof candidate === "string"
                ? Number(candidate.replace(/,/g, ""))
                : typeof candidate === "number"
                ? candidate
                : undefined
            )
            .find((value) => typeof value === "number" && !Number.isNaN(value));

        return { posts, total, totalPages, totalViews };
      } catch (error) {
        console.error("Error fetching posts:", error);
        return { posts: [], total: 0, totalPages: 0, totalViews: 0 };
      }
    },
    options.forceRefresh
  );
};

/**
 * Normalizes a redirect URL to a relative path for Next.js redirect
 * Handles full URLs by extracting the pathname
 */
const normalizeRedirectUrl = (redirectUrl: string): string => {
  try {
    // If it's already a relative path, return as-is (remove trailing slash if present)
    if (redirectUrl.startsWith("/")) {
      return redirectUrl.replace(/\/+$/, "") || "/";
    }
    
    // If it's a full URL, extract the pathname
    const url = new URL(redirectUrl);
    const pathname = url.pathname.replace(/\/+$/, "") || "/";
    return pathname + url.search + url.hash;
  } catch {
    // If parsing fails, assume it's a relative path
    const cleaned = redirectUrl.startsWith("/") ? redirectUrl : `/${redirectUrl}`;
    return cleaned.replace(/\/+$/, "") || "/";
  }
};

/**
 * Extracts the slug from a redirect URL, handling various formats:
 * - Full URL: https://domain.com/blog/new-slug or https://domain.com/api/blog-posts/new-slug
 * - Relative path: /blog/new-slug or /api/blog-posts/new-slug
 * - Just the slug: new-slug
 * Returns the slug string or null if it can't be extracted.
 */
const extractSlugFromRedirectUrl = (redirectUrl: string | null): string | null => {
  if (!redirectUrl) return null;

  try {
    // Remove trailing slashes and trim
    const cleanedUrl = redirectUrl.trim().replace(/\/+$/, "");

    // Try to parse as URL first (handles full URLs)
    let url: URL;
    let pathname: string;
    
    try {
      url = new URL(cleanedUrl);
      pathname = url.pathname;
    } catch {
      // If it's not a full URL, treat it as a relative path
      url = new URL(cleanedUrl, "http://dummy.com");
      pathname = url.pathname;
    }

    // Normalize pathname - remove trailing slashes
    pathname = pathname.replace(/\/+$/, "") || "/";

    // Pattern 1: /api/blog-posts/{slug} or /blog-posts/{slug}
    const apiMatch = pathname.match(/^\/(?:api\/)?blog-posts\/([^/?#]+)$/);
    if (apiMatch && apiMatch[1]) {
      const extracted = decodeURIComponent(apiMatch[1]).trim();
      if (extracted) {
        return extracted;
      }
    }

    // Pattern 2: /blog/{slug} or /blog/{slug}/
    // Handle both with and without trailing slash: /blog/slug or /blog/slug/
    const blogMatch = pathname.match(/^\/blog\/([^/?#]+)$/);
    if (blogMatch && blogMatch[1]) {
      const extracted = decodeURIComponent(blogMatch[1]).trim();
      if (extracted) {
        return extracted;
      }
    }

    // Pattern 3: Just a slug (no leading slash or paths)
    // Check if the pathname is just a single segment that looks like a slug
    const slugOnlyMatch = pathname.match(/^\/([^/?#]+)\/?$/);
    if (slugOnlyMatch && slugOnlyMatch[1]) {
      // Make sure it's not an empty string or just whitespace
      const potentialSlug = decodeURIComponent(slugOnlyMatch[1]).trim();
      if (potentialSlug && !potentialSlug.includes(".")) {
        // Exclude paths that look like files (have extensions)
        return potentialSlug.replace(/\/+$/, "");
      }
    }

    // If redirectUrl is just a slug without any slashes, return it directly
    if (!cleanedUrl.includes("/") && !cleanedUrl.includes("?")) {
      return decodeURIComponent(cleanedUrl);
    }
  } catch (error) {
    console.warn("Failed to extract slug from redirect URL:", redirectUrl, error);
  }

  return null;
};

export interface FetchBlogPostResult {
  post: BlogPost | null;
  redirectUrl?: string;
}

export const fetchBlogPost = async (
  slug: string,
  originalSlug?: string,
  redirectChain: Set<string> = new Set()
): Promise<FetchBlogPostResult> => {
  if (!slug) return { post: null };

  // Prevent infinite redirect loops
  if (redirectChain.has(slug)) {
    console.warn(`Redirect loop detected for slug: ${slug}`);
    return { post: null };
  }

  // Track this slug in the redirect chain
  redirectChain.add(slug);
  const initialSlug = originalSlug || slug;

  try {
    const detailResponse = await fetch(buildUrl(`blog-posts/${slug}`), {
      cache: "no-store",
      redirect: "manual", // Prevent automatic redirect following
    });

    // Handle redirect responses (301, 302, 307, 308)
    if (
      detailResponse.status === 301 ||
      detailResponse.status === 302 ||
      detailResponse.status === 307 ||
      detailResponse.status === 308
    ) {
      // First, check if the redirect response body contains the post data
      // Some APIs return the data in the redirect response body
      try {
        const responseText = await detailResponse.text();
        if (responseText && !responseText.trim().startsWith("<!DOCTYPE")) {
          try {
            const redirectJson = JSON.parse(responseText);
            
            // PRIORITY: If redirect block exists, always redirect (even if post data exists)
            if (redirectJson?.redirect && typeof redirectJson.redirect === "object") {
              const redirectInfo = redirectJson.redirect as { from?: string; to?: string; url?: string };
              if (redirectInfo.url) {
                const normalizedUrl = normalizeRedirectUrl(redirectInfo.url);
                console.log(`Redirect URL found in response: ${slug} -> ${redirectInfo.url} (normalized: ${normalizedUrl})`);
                return { post: null, redirectUrl: normalizedUrl };
              } else if (redirectInfo.to && redirectInfo.to !== slug) {
                // If no URL but we have a 'to' slug, construct the redirect URL
                const redirectUrl = `/blog/${redirectInfo.to}`;
                console.log(`Redirect slug found in response body: ${slug} -> ${redirectInfo.to}`);
                return { post: null, redirectUrl };
              }
            }
            
            // If no redirect, check if the response has the post data structure
            const candidate =
              (redirectJson?.data as Record<string, unknown>) ??
              (redirectJson?.post as Record<string, unknown>) ??
              (redirectJson as Record<string, unknown>);
            
            if (candidate && typeof candidate === "object" && redirectJson?.success !== false) {
              console.log(`Successfully extracted post data from redirect response body for slug: ${slug}`);
              return { post: normalisePost(candidate) };
            }
          } catch {
            // Not valid JSON, continue to Location header check
          }
        }
      } catch {
        // Can't read response body, continue to Location header check
      }
      
      let locationHeader: string | null = null;
      let headerError: unknown = null;
      
      // Try to get Location header, but handle CORS errors gracefully
      try {
        locationHeader = detailResponse.headers.get("Location");
      } catch (err) {
        // CORS might block access to Location header in some browsers
        headerError = err;
        console.warn(`Redirect (${detailResponse.status}) detected but cannot access Location header (possible CORS issue) for slug: ${slug}`, err);
      }

      if (locationHeader) {
        const newSlug = extractSlugFromRedirectUrl(locationHeader);
        
        // Debug logging
        if (!newSlug) {
          console.warn(`Could not extract valid slug from redirect URL: ${locationHeader}`);
          // Fall through to fallback logic
        } else if (newSlug === slug) {
          // Redirect points to same slug - this is a canonical redirect
          // The API might return 308 even for the canonical URL
          // Since the redirect points to the same slug, we should try fetching the API endpoint directly
          // without following redirects, as the redirect might point to a frontend URL (HTML) not the API
          console.log(`Canonical redirect detected (same slug): ${slug}. The API endpoint might return content despite redirect status.`);
          
          try {
            // First, try to read the redirect response body - some APIs include JSON in redirect responses
            try {
              const responseText = await detailResponse.text();
              if (responseText && !responseText.trim().startsWith("<!DOCTYPE")) {
                // Only try to parse if it doesn't look like HTML
                try {
                  const json = JSON.parse(responseText);
                  const candidate =
                    (json?.data as Record<string, unknown>) ??
                    (json?.post as Record<string, unknown>) ??
                    (json as Record<string, unknown>);
              if (candidate && typeof candidate === "object") {
                console.log(`Successfully extracted post from canonical redirect response: ${slug}`);
                return { post: normalisePost(candidate) };
              }
                } catch {
                  // Not valid JSON, continue
                }
              }
            } catch {
              // Can't read response body, continue
            }
            
            // If redirect response doesn't have content, make a fresh request to the API endpoint
            // Use the API URL directly (not following redirects to frontend URLs)
            // The API should return the post data even if it sends a redirect header
            const directApiResponse = await fetch(buildUrl(`blog-posts/${slug}`), {
              cache: "no-store",
              redirect: "manual", // Don't follow redirects - we want the API response
            });
            
            // Check if we get a successful response or another redirect
            if (directApiResponse.ok) {
              const contentType = directApiResponse.headers.get("content-type");
              if (contentType && contentType.includes("application/json")) {
                const json = await directApiResponse.json();
                const candidate =
                  (json?.data as Record<string, unknown>) ??
                  (json?.post as Record<string, unknown>) ??
                  (json as Record<string, unknown>);
                if (candidate && typeof candidate === "object") {
                  console.log(`Successfully fetched post from API endpoint: ${slug}`);
                  return { post: normalisePost(candidate) };
                }
              } else {
                console.warn(`API returned non-JSON content type: ${contentType} for slug: ${slug}`);
              }
            } else if (
              directApiResponse.status === 301 ||
              directApiResponse.status === 302 ||
              directApiResponse.status === 307 ||
              directApiResponse.status === 308
            ) {
              // Still getting redirect - the API might be configured to always redirect
              // In this case, the post might not exist or there's a configuration issue
              console.warn(`API endpoint still returns redirect (${directApiResponse.status}) for slug: ${slug}`);
            } else {
              console.warn(`Failed to fetch from API endpoint. Status: ${directApiResponse.status} for slug: ${slug}`);
            }
          } catch (followError) {
            console.warn("Failed to handle canonical redirect:", followError);
          }
          
          // If canonical redirect handling didn't work, continue to fallback
          // The fallback will try to find the post in the posts list
        } else {
          // Recursively fetch the redirected post
          console.log(`Redirect detected: ${slug} -> ${newSlug} (from Location: ${locationHeader})`);
          return fetchBlogPost(newSlug, initialSlug, redirectChain);
        }
      } else if (headerError) {
        // If we have a CORS error accessing the header, we can't get the redirect target
        // In this case, try the fallback approach - search in the posts list
        // This might find the redirected post if it exists in the list
        console.warn(`CORS error accessing redirect Location header. Attempting fallback for slug: ${slug}`);
      } else {
        console.warn(`Redirect response (${detailResponse.status}) but no Location header for slug: ${slug}`);
      }
    }

    // Handle successful response (200 OK)
    if (detailResponse.ok) {
      const json = await detailResponse.json();
      
      // PRIORITY: Check for redirect block first (even in successful responses)
      if (json?.redirect && typeof json.redirect === "object") {
        const redirectInfo = json.redirect as { from?: string; to?: string; url?: string };
        if (redirectInfo.url) {
          const normalizedUrl = normalizeRedirectUrl(redirectInfo.url);
          console.log(`Redirect URL found in successful response: ${slug} -> ${redirectInfo.url} (normalized: ${normalizedUrl})`);
          return { post: null, redirectUrl: normalizedUrl };
        } else if (redirectInfo.to && redirectInfo.to !== slug) {
          // If no URL but we have a 'to' slug, construct the redirect URL
          const redirectUrl = `/blog/${redirectInfo.to}`;
          console.log(`Redirect slug found in successful response: ${slug} -> ${redirectInfo.to}`);
          return { post: null, redirectUrl };
        }
      }
      
      // If no redirect, return the post data
      const candidate =
        (json?.data as Record<string, unknown>) ??
        (json?.post as Record<string, unknown>) ??
        (json as Record<string, unknown>);
      if (candidate && typeof candidate === "object") {
        return { post: normalisePost(candidate) };
      }
    }

    // If we got a redirect status but couldn't handle it, or any other non-OK status,
    // log it for debugging (but don't fail - let fallback handle it)
    if (!detailResponse.ok && detailResponse.status !== 301 && detailResponse.status !== 302 && 
        detailResponse.status !== 307 && detailResponse.status !== 308) {
      console.warn(`API returned non-OK status ${detailResponse.status} for slug: ${slug}`);
    }
  } catch (error) {
    // Enhanced error handling for CORS and network errors
    if (error instanceof TypeError && (error.message.includes("Failed to fetch") || error.message.includes("CORS"))) {
      console.warn("Network/CORS error when fetching blog post:", error);
      // This might be a CORS issue, continue to fallback
    } else if (error instanceof Error && error.name === "NetworkError") {
      console.warn("Network error when fetching blog post:", error);
    } else {
    console.warn("Detail endpoint failed, falling back to list fetch:", error);
    }
  }

  // Fallback: try to find in the full posts list
  const { posts } = await fetchBlogPosts({ perPage: 100 }, { forceRefresh: true });
  const foundPost = posts.find((post) => post.slug === slug || post.slug === initialSlug);
  return { post: foundPost ?? null };
};

export const getBlogCategories = (posts: BlogPost[]): BlogCategory[] => {
  const counts: Record<string, number> = {};
  posts.forEach((post) => {
    counts[post.category] = (counts[post.category] || 0) + 1;
  });
  return Object.entries(counts).map(([name, count], index) => ({
    id: (index + 1).toString(),
    name,
    count,
  }));
};

export const calculateTotalViews = (posts: BlogPost[]): number =>
  posts.reduce((sum, post) => sum + (Number(post.views) || 0), 0);

export const getPopularPosts = (posts: BlogPost[], limit = 7): BlogPost[] =>
  [...posts].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, limit);

export interface BlogCategorySummary {
  total: number;
  categories: BlogCategory[];
}

export const fetchBlogCategorySummary = async (
  options: { forceRefresh?: boolean } = {}
): Promise<BlogCategorySummary> =>
  resolveCache(categorySummaryCache, async () => {
    try {
      const response = await fetch(buildUrl("blog-posts/categories"), {
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch blog categories: ${response.status} ${response.statusText}`
        );
      }

      const json = await response.json();

      const totalRaw =
        json?.total ?? json?.count ?? json?.totalPosts ?? json?.pagination?.total;

      const total =
        typeof totalRaw === "string"
          ? Number(totalRaw)
          : typeof totalRaw === "number"
          ? totalRaw
          : 0;

      const categoriesRaw = Array.isArray(json?.categories)
        ? json.categories
        : Array.isArray(json?.data)
        ? json.data
        : [];

      const categories: BlogCategory[] = (categoriesRaw as Record<
        string,
        unknown
      >[]).map((item, index): BlogCategory => {
          const id =
            (typeof item.id === "string" && item.id) ||
            (typeof item.id === "number" && String(item.id)) ||
            (typeof item.slug === "string" && item.slug) ||
            String(index + 1);

          const nameRaw =
            (typeof item.name === "string" && item.name) ||
            (typeof item.title === "string" && item.title) ||
            "Uncategorized";

          const slugRaw =
            (typeof item.slug === "string" && item.slug) ||
            slugify(nameRaw) ||
            id;

          return {
            id,
            name: decodeHtmlEntities(nameRaw),
            count:
              typeof item.count === "string"
                ? Number(item.count)
                : typeof item.count === "number"
                ? item.count
                : 0,
            slug: slugRaw,
          };
        });

      categories.sort((a: BlogCategory, b: BlogCategory) =>
        a.name.localeCompare(b.name)
      );

      const computedTotal =
        total && !Number.isNaN(total) && total > 0
          ? total
          : categories.reduce((sum, cat) => sum + (Number(cat.count) || 0), 0);

      return { total: computedTotal, categories };
    } catch (error) {
      console.error("Error fetching category summary:", error);
      return { total: 0, categories: [] };
    }
  }, options.forceRefresh);

export const fetchPopularPosts = async (
  limit = 7,
  options: { forceRefresh?: boolean } = {}
): Promise<BlogPost[]> => {
  const cacheKey = limit;
  if (!popularPostsCache.has(cacheKey)) {
    popularPostsCache.set(cacheKey, { value: null, promise: null });
  }
  const cacheState = popularPostsCache.get(cacheKey)!;

  return resolveCache(
    cacheState,
    async () => {
      try {
        const response = await fetch(
          buildUrl("blog-posts/popular", { limit }),
          { cache: "no-store" }
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch popular posts: ${response.status} ${response.statusText}`
          );
        }

        const json = await response.json();
        const popularRaw = pickPostsArray(json);

        if (popularRaw.length === 0 && Array.isArray(json?.data)) {
          return (json.data as Record<string, unknown>[]).map(normalisePost);
        }

        return popularRaw.map(normalisePost);
      } catch (error) {
        console.error("Error fetching popular posts:", error);
        return [];
      }
    },
    options.forceRefresh
  );
};

export const fetchTotalViews = async (
  options: { forceRefresh?: boolean } = {}
): Promise<number> =>
  resolveCache(
    totalViewsCache,
    async () => {
      try {
        const response = await fetch(buildUrl("blog-posts/stats/total-views"), {
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch total views: ${response.status} ${response.statusText}`
          );
        }

        const json = await response.json();
        const rawTotal =
          json?.totalViews ??
          json?.total_views ??
          json?.data ??
          json?.total ??
          0;

        const total =
          typeof rawTotal === "string"
            ? Number(rawTotal.replace(/,/g, ""))
            : typeof rawTotal === "number"
            ? rawTotal
            : 0;

        return Number.isFinite(total) ? total : 0;
      } catch (error) {
        console.error("Error fetching total views:", error);
        return 0;
      }
    },
    options.forceRefresh
  );

export interface BlogComment {
  id: string;
  author: string;
  authorAvatar?: string;
  content: string;
  createdAt: string;
}

export const fetchPostComments = async (
  slug: string
): Promise<BlogComment[]> => {
  if (!slug) {
    return [];
  }

  const endpoints = [
    buildUrl(`blog-posts/${slug}/comments`),
    buildUrl(`blog-posts/${slug}/comment`),
  ];

  for (const endpoint of endpoints) {
    try {
      const response = await fetch(endpoint, {
        cache: "no-store",
      });

      if (!response.ok) {
        if (endpoint.endsWith("/comment")) {
          throw new Error(
            `Failed to fetch comments: ${response.status} ${response.statusText}`
          );
        }
        continue;
      }

      const json = await response.json();
      const commentsRaw = (json?.data ?? json?.comments ?? []) as Record<
        string,
        unknown
      >[];

      return commentsRaw.map((item, index) => {
        const author =
          (typeof item.author === "string" && item.author) ||
          (typeof item.comment_author === "string" && item.comment_author) ||
          "Anonymous";
        const avatar =
          (typeof item.author_avatar === "string" && item.author_avatar) ||
          (typeof item.comment_author_avatar === "string" &&
            item.comment_author_avatar) ||
          undefined;
        const content =
          (typeof item.content === "string" && item.content) ||
          (typeof item.comment_content === "string" && item.comment_content) ||
          "";
        const createdAtRaw =
          item.created_at ||
          item.comment_date ||
          item.date ||
          item.comment_date_gmt ||
          new Date().toISOString();

        const parent =
          (typeof item.parentId === "string" && item.parentId) ||
          (typeof item.parentId === "number" && String(item.parentId)) ||
          (typeof item.parent_id === "string" && item.parent_id) ||
          (typeof item.parent_id === "number" && String(item.parent_id)) ||
          (typeof item.comment_parent === "string" && item.comment_parent) ||
          (typeof item.comment_parent === "number" &&
            String(item.comment_parent)) ||
          undefined;

        return {
          id:
            (typeof item.id === "string" && item.id) ||
            (typeof item.comment_ID === "string" && item.comment_ID) ||
            String(index + 1),
          author,
          authorAvatar: avatar,
          content,
          createdAt: new Date(createdAtRaw as string).toISOString(),
          parentId: parent,
        };
      });
    } catch (error) {
      if (endpoint.endsWith("/comment")) {
        console.error("Error fetching comments:", error);
      }
    }
  }

  return [];
};

